<template>
  <div class="onb" :class="`onb--${phase}`">
    <!-- Fondo oscuro de la apertura y la celebración; en los pasos se desvanece a blanco. -->
    <div class="onb__black" :class="{ 'onb__black--on': dark }"></div>
    <div class="onb__bg" :class="{ 'onb__bg--on': dark }"></div>

    <transition name="intro">
      <OnboardingIntro v-if="phase === 'intro'" @start="start" @skip="skip" />
    </transition>

    <div v-if="phase === 'steps'" class="onb__shell">
      <OnboardingStepper :current="step" />

      <div class="onb__stage">
        <transition :name="`slide-${dir}`">
          <div :key="step" class="onb__step">
            <StepPark v-if="step === 0" />
            <StepPlots v-else-if="step === 1" />
            <StepTenant v-else-if="step === 2" />
            <StepContract v-else-if="step === 3" />
            <StepBilling v-else />
          </div>
        </transition>
      </div>

      <footer class="onb__foot">
        <button v-if="step > 0" type="button" class="ob-btn ob-btn--ghost" :disabled="ob.busy" @click="back">← Anterior</button>
        <span v-else></span>
        <button v-if="step > 0" type="button" class="ob-link ob-link--gray" :disabled="ob.busy" @click="omit">Omitir este paso</button>
        <button type="button" class="ob-btn" :disabled="ob.busy" @click="next">{{ ob.busy ? 'Guardando…' : 'Continuar →' }}</button>
      </footer>
    </div>

    <transition name="intro">
      <div v-if="phase === 'team'" class="onb__team"><TeamInvite @later="finish" @done="finish" /></div>
    </transition>

    <transition name="intro">
      <OnboardingDone v-if="phase === 'done'" />
    </transition>
  </div>
</template>

<script setup>
import { API_ORIGIN } from '../config'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import OnboardingIntro from '@/components/Onboarding/OnboardingIntro.vue'
import OnboardingStepper from '@/components/Onboarding/OnboardingStepper.vue'
import StepPark from '@/components/Onboarding/StepPark.vue'
import StepPlots from '@/components/Onboarding/StepPlots.vue'
import StepTenant from '@/components/Onboarding/StepTenant.vue'
import StepContract from '@/components/Onboarding/StepContract.vue'
import StepBilling from '@/components/Onboarding/StepBilling.vue'
import TeamInvite from '@/components/Onboarding/TeamInvite.vue'
import OnboardingDone from '@/components/Onboarding/OnboardingDone.vue'
import '@/components/Onboarding/onboarding.css'
import { ob, resetOnboarding, newPlotRow } from '@/services/onboardingStore'
import { useAuthStore } from '@/services/authStore'
import { getParkFullConfig } from '@/services/parkConfig'
import { getParkById } from '@/services/parks'
import { completeOnboarding } from '@/services/onboarding'
import { SUBMITTERS } from '@/components/Onboarding/onboardingFlow'
import { ZONE_PRESETS, CUSTOM_ZONE, STEPS } from '@/components/Onboarding/onboardingFormat'

const router = useRouter()
const { state, refreshUser } = useAuthStore()

const phase = ref('intro') // intro | steps | team | done
const step = ref(0)
const dir = ref('forward')
const mounted = ref(false)
let doneTimer = null

// El fondo oscuro aparece con la apertura (una vez montada la vista) y vuelve en la celebración.
const dark = computed(() => mounted.value && (phase.value === 'intro' || phase.value === 'done'))

const FRIENDLY = {
  DUPLICATE_PLOT_NAME: 'Ya hay una nave con ese código. Cada nave necesita uno distinto.',
  INVALID_AREA: 'Los metros cuadrados deben ser un número mayor a 0.',
  PLOT_NAME_REQUIRED: 'Cada nave necesita un código.',
}

// Trae lo que el parque ya tiene (por si el administrador cerró el asistente a la mitad).
async function loadExisting() {
  try {
    const [config, park] = await Promise.all([getParkFullConfig(ob.parkId), getParkById(ob.parkId)])
    Object.assign(ob.park, {
      name: config.name || '',
      rfc: config.rfc || '',
      contactEmail: config.contactEmail || '',
      address: config.address || '',
      city: config.city || '',
      state: config.state || '',
      bankName: config.bankName || '',
      bankBeneficiary: config.bankBeneficiary || '',
      bankClabe: String(config.bankClabe || '').replace(/\D/g, ''),
      logoUrl: config.logoUrl || '',
      logoPreview: config.logoUrl ? `${API_ORIGIN}/uploads/${config.logoUrl}` : '',
    })
    const rows = (park.plots || []).map((p) => {
      const row = newPlotRow()
      row.name = p.name
      row.areaM2 = p.areaM2 ? String(p.areaM2) : ''
      const zone = p.internalLocation || ''
      if (ZONE_PRESETS.includes(zone)) row.zone = zone
      else if (zone) { row.zone = CUSTOM_ZONE; row.customZone = zone }
      row.saved = true
      row.id = p.id
      row.occupied = !!p.currentTenant || (p.status && p.status !== 'VACANT')
      return row
    })
    ob.plots = [...rows, newPlotRow()]
  } catch {
    // Sin datos previos el asistente arranca vacío; el administrador puede llenarlo igual.
  }
}

onMounted(async () => {
  resetOnboarding()
  ob.parkId = state.user.parkId
  ob.userName = state.user.name || ''
  ob.park.name = state.user.parkName || ''
  requestAnimationFrame(() => { mounted.value = true })
  await loadExisting()
  ob.loaded = true
})

onBeforeUnmount(() => clearTimeout(doneTimer))

function start() {
  phase.value = 'steps'
}

async function leave() {
  await refreshUser()
  try { sessionStorage.setItem('parkosFromOnboarding', '1') } catch { /* sin almacenamiento: solo se pierde la animación */ }
  router.replace('/inicio')
}

async function skip() {
  try {
    await completeOnboarding(ob.parkId, true)
  } catch {
    // Si no se pudo guardar, el asistente volverá a aparecer la próxima vez; no bloquea la entrada.
  }
  await leave()
}

async function next() {
  ob.error = ''
  ob.busy = true
  let ok = false
  try {
    ok = await SUBMITTERS[step.value]()
  } catch (e) {
    ob.error = FRIENDLY[e.code] || e.message || 'No se pudo guardar. Intenta de nuevo.'
  } finally {
    ob.busy = false
  }
  if (!ok) return
  advance()
}

function advance() {
  ob.error = ''
  dir.value = 'forward'
  if (step.value < STEPS.length - 1) step.value += 1
  else phase.value = 'team'
}

function omit() {
  advance()
}

function back() {
  if (step.value === 0 || ob.busy) return
  ob.error = ''
  dir.value = 'back'
  step.value -= 1
}

async function finish() {
  phase.value = 'done'
  try {
    await completeOnboarding(ob.parkId, false)
  } catch {
    // Si falla, se reintenta en la próxima sesión; la celebración sigue.
  }
  doneTimer = setTimeout(leave, 3000)
}
</script>

<style scoped>
.onb { position: fixed; inset: 0; z-index: 2000; background: #fff; overflow: hidden; font-family: Inter, sans-serif; }

.onb__black { position: absolute; inset: 0; background: #000; opacity: 0; transition: opacity 0.5s ease; }
.onb__black--on { opacity: 1; }
.onb__bg {
  position: absolute; inset: 0; opacity: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1e40af 100%);
  background-size: 200% 200%;
  transition: opacity 0.5s ease;
}
.onb__bg::after { content: ''; position: absolute; inset: 0; background-image: radial-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px); background-size: 24px 24px; }
.onb__bg--on { opacity: 1; }
/* La apertura: el negro da paso al degradado (que se "mueve" durante 2 s). */
.onb--intro .onb__bg--on { transition: opacity 1.2s ease 0.3s; animation: drift 2s ease-out both; }
@keyframes drift { from { background-position: 0% 0%; } to { background-position: 60% 60%; } }

.onb__shell { position: absolute; inset: 0; display: flex; flex-direction: column; }
.onb__stage { position: relative; flex: 1; min-height: 0; overflow: hidden; }
.onb__step { position: absolute; inset: 0; overflow-y: auto; display: flex; }
.onb__foot { height: 64px; flex-shrink: 0; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 12px; padding: 0 28px; background: #fff; border-top: 1px solid #f1f5f9; }
.onb__foot > :first-child { justify-self: start; }
.onb__foot > :last-child { justify-self: end; }
.onb__team { position: absolute; inset: 0; background: #fff; }

/* Entre pasos: el actual sale hacia la izquierda y el siguiente entra por la derecha (al revés al retroceder). */
.slide-forward-enter-active, .slide-forward-leave-active, .slide-back-enter-active, .slide-back-leave-active { transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out; }
.slide-forward-enter-from, .slide-back-leave-to { transform: translateX(60px); opacity: 0; }
.slide-forward-leave-to, .slide-back-enter-from { transform: translateX(-60px); opacity: 0; }

.intro-enter-active { transition: opacity 0.3s ease 0.2s, transform 0.3s ease 0.2s; }
.intro-leave-active { transition: opacity 0.4s ease; position: absolute; inset: 0; }
.intro-enter-from { opacity: 0; transform: translateX(40px); }
.intro-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .onb__foot { padding: 0 12px; grid-template-columns: auto 1fr auto; }
}
@media (prefers-reduced-motion: reduce) {
  .onb__black, .onb__bg, .onb--intro .onb__bg--on { transition: none; animation: none; }
  .slide-forward-enter-active, .slide-forward-leave-active, .slide-back-enter-active, .slide-back-leave-active,
  .intro-enter-active, .intro-leave-active { transition: none; }
}
</style>